/**
 * `/admin/exports` — regulator audit-pack surface (US-ADM-14, US-REG-01).
 *
 * One job, one button: build an audit pack `.zip` (workbooks + every
 * evidence file + manifest.json + summary.txt). Admins click the
 * button, the worker builds the artefact in the background, and the
 * Recent exports list re-renders to surface the row. Each row carries
 * a stable login-gated download path; the SPA renders it as an
 * `<a download>` link and the browser sends the admin session cookie
 * automatically.
 *
 * The earlier two-mode flow (Path A "dashboard + audit log Excel" vs
 * Path B "regulator audit pack zip") + per-row "re-send link" action
 * was retired in April 2026. Operational reads come from the dashboard
 * directly; the regulator zip is the only export shape, and an
 * authenticated download endpoint replaces presigned URLs entirely
 * (so a stale email link is harmless without a valid session).
 *
 * 401 from any call short-circuits to `/admin/sign-in`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { ExportListEntry, ExportListResponse, JobRunStatus } from '@isms/shared';

export interface AdminExportsPageProps {
  client: ApiClient;
  /** Where to send the admin on 401. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; list: ExportListResponse };

type RequestStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; jobRunId: string; exportRequestId: string }
  | { kind: 'error'; error: ApiError | string };

const STATUS_LABELS: Record<JobRunStatus, string> = {
  pending: 'Queued',
  running: 'Building…',
  succeeded: 'Ready',
  failed: 'Failed',
};

export function AdminExportsPage({
  client,
  signInPath = '/admin/sign-in',
}: AdminExportsPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [requestStatus, setRequestStatus] = useState<RequestStatus>({ kind: 'idle' });
  const [packOpen, setPackOpen] = useState(false);
  const [packFrom, setPackFrom] = useState('');
  const [packTo, setPackTo] = useState('');
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    try {
      const list = await client.listAdminExports();
      setState({ kind: 'ready', list });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load your recent exports. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = useCallback(async () => {
    const fromTrimmed = packFrom.trim();
    const toTrimmed = packTo.trim();
    if (fromTrimmed && toTrimmed && fromTrimmed > toTrimmed) {
      setRequestStatus({
        kind: 'error',
        error: 'The "from" date must be on or before the "to" date.',
      });
      return;
    }
    setRequestStatus({ kind: 'pending' });
    try {
      const { job_run_id, export_request_id } = await client.requestAuditPack({
        audit_log_from: fromTrimmed ? fromTrimmed : null,
        audit_log_to: toTrimmed ? toTrimmed : null,
        person_ids: null,
        requirement_ids: null,
      });
      setRequestStatus({
        kind: 'success',
        jobRunId: idToString(job_run_id),
        exportRequestId: export_request_id,
      });
      setPackFrom('');
      setPackTo('');
      setPackOpen(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setRequestStatus({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't start that audit pack. Please check your connection and try again.",
      });
    }
  }, [client, load, packFrom, packTo, redirectToSignIn]);

  return (
    <main className="admin-exports" aria-labelledby="admin-exports-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-exports-header">
        <h1 id="admin-exports-heading">Audit pack exports</h1>
        <p className="admin-exports-intro">
          Build a regulator-ready snapshot of the compliance register: workbooks for compliance
          rows, the audit log, policy versions, and acknowledgement signatures, plus every evidence
          file and a <code>manifest.json</code> of per-file SHA-256 hashes the auditor can verify.
          Builds run in the background; the row appears in the list below as soon as it's queued.
          Once it's ready, click <strong>Download</strong> on the row to fetch the zip — you need to
          be signed in for the download to work.
        </p>
      </header>

      <section className="admin-exports-action" aria-labelledby="admin-exports-action-heading">
        <h2 id="admin-exports-action-heading">Start an audit pack</h2>
        {packOpen ? (
          <div className="admin-exports-audit-pack-form">
            <p className="admin-exports-action-hint">
              Leave both dates blank to include the full audit log.
            </p>
            <div className="admin-exports-audit-pack-fields">
              <label className="admin-exports-audit-pack-field">
                <span>Audit log from (optional)</span>
                <input
                  type="date"
                  value={packFrom}
                  onChange={(e) => setPackFrom(e.target.value)}
                  disabled={requestStatus.kind === 'pending'}
                />
              </label>
              <label className="admin-exports-audit-pack-field">
                <span>Audit log to (optional)</span>
                <input
                  type="date"
                  value={packTo}
                  onChange={(e) => setPackTo(e.target.value)}
                  disabled={requestStatus.kind === 'pending'}
                />
              </label>
            </div>
            <div className="admin-exports-audit-pack-actions">
              <button
                type="button"
                className="admin-exports-primary-button"
                onClick={() => void handleSubmit()}
                disabled={requestStatus.kind === 'pending'}
              >
                {requestStatus.kind === 'pending' ? 'Queuing…' : 'Build audit pack'}
              </button>
              <button
                type="button"
                className="admin-exports-secondary-button"
                onClick={() => setPackOpen(false)}
                disabled={requestStatus.kind === 'pending'}
              >
                Cancel
              </button>
            </div>
            {requestStatus.kind === 'error' ? <ErrorBanner error={requestStatus.error} /> : null}
          </div>
        ) : (
          <button
            type="button"
            className="admin-exports-primary-button"
            onClick={() => {
              setPackOpen(true);
              setRequestStatus({ kind: 'idle' });
            }}
          >
            Build audit pack
          </button>
        )}
        {requestStatus.kind === 'success' ? (
          <p className="admin-exports-action-success" role="status">
            We've queued your audit pack (run #{requestStatus.jobRunId} ·{' '}
            {requestStatus.exportRequestId}). It'll appear in the list below as it builds.
          </p>
        ) : null}
      </section>

      <section className="admin-exports-list-section" aria-labelledby="admin-exports-list-heading">
        <h2 id="admin-exports-list-heading">Recent exports</h2>
        {state.kind === 'loading' ? <p>Loading exports…</p> : null}
        {state.kind === 'error' ? (
          <div>
            <ErrorBanner error={state.error} />
            <button type="button" onClick={() => void load()}>
              Retry
            </button>
          </div>
        ) : null}
        {state.kind === 'ready' && state.list.items.length === 0 ? (
          <p className="admin-exports-empty">
            No exports yet. Click <strong>Build audit pack</strong> above to queue your first one.
          </p>
        ) : null}
        {state.kind === 'ready' && state.list.items.length > 0 ? (
          <ul className="admin-exports-list">
            {state.list.items.map((entry) => (
              <ExportRow key={entry.export_request_id} entry={entry} />
            ))}
          </ul>
        ) : null}
      </section>
    </main>
  );
}

interface ExportRowProps {
  entry: ExportListEntry;
}

function ExportRow({ entry }: ExportRowProps): JSX.Element {
  const requestedAt = useMemo(() => formatTimestamp(entry.requested_at), [entry.requested_at]);
  const completedAt = useMemo(
    () => (entry.completed_at ? formatTimestamp(entry.completed_at) : null),
    [entry.completed_at],
  );
  return (
    <li className="admin-exports-row">
      <div className="admin-exports-row-head">
        <span className="admin-exports-row-id">{entry.export_request_id}</span>
        <span className={`admin-exports-row-status admin-exports-status-${entry.status}`}>
          {STATUS_LABELS[entry.status]}
        </span>
      </div>
      <dl className="admin-exports-row-meta">
        <dt>Requested</dt>
        <dd>{requestedAt}</dd>
        {completedAt ? (
          <>
            <dt>Completed</dt>
            <dd>{completedAt}</dd>
          </>
        ) : null}
      </dl>
      {entry.download_path ? (
        <p className="admin-exports-row-link">
          <a href={entry.download_path} download>
            Download audit pack
          </a>
        </p>
      ) : null}
    </li>
  );
}

function formatTimestamp(v: string | Date): string {
  const d = typeof v === 'string' ? new Date(v) : v;
  return d.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function idToString(v: string | number | bigint): string {
  return typeof v === 'string' ? v : String(v);
}
